import React from "react";
import "./Table.css";

export type TableColumn<T> = {
  id: string;
  header: React.ReactNode;
  render: (row: T, index: number) => React.ReactNode;
  headerClassName?: string;
  cellClassName?: string;
};

type TableProps<T> = {
  columns: TableColumn<T>[];
  data: T[];
  getRowKey: (row: T, index: number) => React.Key;
  gridTemplateColumns: string;
  wrapperClassName?: string;
  tableClassName?: string;
  headerRowClassName?: string;
  bodyRowClassName?: string;
  linkRowClassName?: string;
  rowClassName?: (row: T, index: number) => string | undefined;
  rowHref?: (row: T, index: number) => string | undefined;
  beforeHeader?: React.ReactNode;
  stickyHeaderTop?: number;
};

function joinClasses(
  ...classNames: Array<string | undefined | null | false>
): string {
  return classNames.filter(Boolean).join(" ");
}

export default function Table<T>({
  columns,
  data,
  getRowKey,
  gridTemplateColumns,
  wrapperClassName,
  tableClassName,
  headerRowClassName,
  bodyRowClassName,
  linkRowClassName,
  rowClassName,
  rowHref,
  beforeHeader,
  stickyHeaderTop = 0
}: TableProps<T>) {
  const rowStyle = {
    "--table-columns": gridTemplateColumns
  } as React.CSSProperties;

  const headerStyle = {
    ...rowStyle,
    "--table-header-top": `${stickyHeaderTop}px`
  } as React.CSSProperties;

  return (
    <div className={joinClasses("table-wrapper", wrapperClassName)}>
      <div className={joinClasses("table-root", tableClassName)}>
        {beforeHeader}

        <div
          className={joinClasses("table-row", "table-header-row", headerRowClassName)}
          style={headerStyle}
        >
          {columns.map((column) => (
            <div
              className={joinClasses(
                "table-cell",
                "table-header-cell",
                column.headerClassName
              )}
              key={column.id}
            >
              {column.header}
            </div>
          ))}
        </div>

        {data.map((row, index) => {
          const href = rowHref?.(row, index);
          const currentRowClassName = joinClasses(
            "table-row",
            "table-body-row",
            bodyRowClassName,
            rowClassName?.(row, index)
          );

          const rowCells = columns.map((column) => (
            <div
              className={joinClasses("table-cell", column.cellClassName)}
              key={column.id}
            >
              {column.render(row, index)}
            </div>
          ));

          if (href) {
            return (
              <a
                className={joinClasses(
                  currentRowClassName,
                  "table-link-row",
                  linkRowClassName
                )}
                href={href}
                key={getRowKey(row, index)}
                style={rowStyle}
              >
                {rowCells}
              </a>
            );
          }

          return (
            <div
              className={currentRowClassName}
              key={getRowKey(row, index)}
              style={rowStyle}
            >
              {rowCells}
            </div>
          );
        })}
      </div>
    </div>
  );
}
